import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Session, PatientInfo, SubtestScores, CompositeScores, Interpretation } from '../types';

interface SessionState {
  sessions: Session[];
  currentSessionId: string | null;
  maxSessions: number;

  // Actions
  saveSession: (name: string, patientInfo: PatientInfo, subtestScores: SubtestScores, compositeScores: CompositeScores, interpretations?: Interpretation[]) => string;
  loadSession: (id: string) => Session | null;
  updateSession: (id: string, updates: Partial<Session>) => void;
  deleteSession: (id: string) => void;
  duplicateSession: (id: string) => string | null;
  exportSession: (id: string) => string | null;
  importSession: (data: string) => Session | null;
  getCurrentSession: () => Session | null;
  setCurrentSession: (id: string | null) => void;
  getRecentSessions: (limit?: number) => Session[];
  clearAllSessions: () => void;
}

const generateId = () => Math.random().toString(36).substring(2, 11);

export const useSessionStore = create<SessionState>()(
  persist(
    (set, get) => ({
      sessions: [],
      currentSessionId: null,
      maxSessions: 50,

      saveSession: (name, patientInfo, subtestScores, compositeScores, interpretations = []) => {
        const id = generateId();
        const now = new Date();

        const newSession: Session = {
          id,
          name: name || `Sesja ${patientInfo.name || 'Bez nazwy'} - ${now.toLocaleDateString('pl-PL')}`,
          patientInfo,
          subtestScores,
          compositeScores,
          interpretations,
          createdAt: now,
          updatedAt: now,
        };

        set((state) => {
          let sessions = [newSession, ...state.sessions];

          // Enforce max sessions limit
          if (sessions.length > state.maxSessions) {
            sessions = sessions.slice(0, state.maxSessions);
          }

          return {
            sessions,
            currentSessionId: id,
          };
        });

        return id;
      },

      loadSession: (id) => {
        const session = get().sessions.find((s) => s.id === id);
        if (session) {
          set({ currentSessionId: id });
        }
        return session || null;
      },

      updateSession: (id, updates) => {
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === id ? { ...s, ...updates, updatedAt: new Date() } : s
          ),
        }));
      },

      deleteSession: (id) => {
        set((state) => ({
          sessions: state.sessions.filter((s) => s.id !== id),
          currentSessionId: state.currentSessionId === id ? null : state.currentSessionId,
        }));
      },

      duplicateSession: (id) => {
        const session = get().sessions.find((s) => s.id === id);
        if (!session) return null;

        const newId = generateId();
        const now = new Date();

        const duplicated: Session = {
          ...session,
          id: newId,
          name: `${session.name} (kopia)`,
          createdAt: now,
          updatedAt: now,
        };

        set((state) => ({
          sessions: [duplicated, ...state.sessions].slice(0, state.maxSessions),
          currentSessionId: newId,
        }));

        return newId;
      },

      exportSession: (id) => {
        const session = get().sessions.find((s) => s.id === id);
        if (!session) return null;

        return JSON.stringify(session, null, 2);
      },

      importSession: (data) => {
        try {
          const parsed = JSON.parse(data) as Session;

          // Validate required fields
          if (!parsed.patientInfo || !parsed.subtestScores || !parsed.compositeScores) {
            return null;
          }

          const newId = generateId();
          const now = new Date();

          const imported: Session = {
            ...parsed,
            id: newId,
            name: `${parsed.name || 'Import'} (zaimportowany)`,
            createdAt: now,
            updatedAt: now,
          };

          set((state) => ({
            sessions: [imported, ...state.sessions].slice(0, state.maxSessions),
            currentSessionId: newId,
          }));

          return imported;
        } catch {
          return null;
        }
      },

      getCurrentSession: () => {
        const { sessions, currentSessionId } = get();
        return sessions.find((s) => s.id === currentSessionId) || null;
      },

      setCurrentSession: (id) => set({ currentSessionId: id }),

      getRecentSessions: (limit = 10) => {
        return get().sessions.slice(0, limit);
      },

      clearAllSessions: () => set({ sessions: [], currentSessionId: null }),
    }),
    {
      name: 'wiscv-sessions',
      storage: createJSONStorage(() => localStorage),
    }
  )
);
